import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import newlogo from "./../../assets/Images/sign-in-logo.svg";
import GlobalLoader from "../shared/GlobalLoader";
import LogoutAlert from "./../auth/LogoutAlert";
import { useLoading } from "../../context/LoadingContext";
import { useAppContext } from "../../context/AppContext.tsx";

const Facility = () => {
  const baseUrl = process.env.REACT_APP_BASE_URL;
  const selectedCustomerId = localStorage.getItem("customerId") || 0;
  const roleName = localStorage.getItem("role_name") || "";
  const [showLogoutAlert, setShowLogoutAlert] = useState(false);
  const [theme, setTheme] = useState(localStorage.getItem("theme") || "light");
  const [adminCustomers, setAdminCustomers] = useState([]);
  const [adminCustomerError, setAdminCustomerError] = useState(null);

  const navigate = useNavigate();
  const locationPath = useLocation();
  const { isLoading, setLoading } = useLoading();
    const {
    customer,
    setCustomer,
    locations,
    setLocations,
    location,
    setLocation,
    systems,
    setSystems,
    system,
    setSystem,
    errorMessage,
    setErrorMessage,
    systemsLoading,
    setSystemsLoading,
  } = useAppContext();

useEffect(() => {
  const fetchAdminCustomers = async () => {
    setLoading(true);
    try {
      const accessToken = sessionStorage.getItem("accessToken");
      const userId = parseInt(localStorage.getItem("id"));

      const headers = accessToken
        ? { Authorization: `${accessToken}` }
        : {};

      const response = await axios.post(
        `${baseUrl}/customer`,
        { userId }, 
        { headers }
      );

      const customerList = response?.data || [];
      if(!customerList.length) setErrorMessage("For customer assistance, kindly reach out to the admin");
      setAdminCustomers(customerList);

      if (customerList.length > 0) {
        const storedCustomerId = localStorage.getItem("customerId");

        const storedCustomer = customerList.find(
          (c) => c.id === parseInt(storedCustomerId)
        );

        setCustomer(
          storedCustomer
            ? storedCustomer.name
            : customerList[0].name
        );
      }

    } catch (error) {
      console.error("Error fetching admin customer data:", error);
      setAdminCustomerError(error.message);
    } finally {
      setLoading(false);
    }
  };

    fetchAdminCustomers();
}, []);

  useEffect(() => {
    if (!customer || adminCustomers.length === 0) return;

    const selectedCustomer = adminCustomers.find((c) => c.name === customer);
    if (selectedCustomer?.id) {
      fetchLocations(selectedCustomer.id);
    }
  }, [customer, adminCustomers]);

  const fetchLocations = async (customerId) => {
    try {
      setLoading(true);
      const accessToken = sessionStorage.getItem("accessToken");
      const headers = accessToken ? { Authorization: `${accessToken}` } : {};
      const userId = parseInt(localStorage.getItem("id"));
      const response = await axios.post(
        `${baseUrl}/customer/locations/${customerId}`,
        { userId }, 
        { headers }
      );
      const locationData = response?.data || [];
      setLocations(locationData);

      const storedLocationId = localStorage.getItem("selectedLocationId");
      if (storedLocationId) {
        const storedLocation = locationData.find(
          (loc) => loc.id === parseInt(storedLocationId)
        );
        setLocation(storedLocation ? storedLocation.plant_name : locationData[0]?.plant_name || "");
      } else {
        setLocation(locationData[0]?.plant_name || "");
      }
      setErrorMessage("");
      setLoading(false);
    } catch (error) {
      console.error("Error fetching locations:", error);
      setLocations([]);
      setErrorMessage("Failed to fetch locations.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    document.documentElement.setAttribute("data-theme", theme);
  }, [theme]);


  const selectedLocation = locations.find((loc) => loc.plant_name === location);
  localStorage.setItem("location", JSON.stringify(selectedLocation))
  const selectedLocationId = selectedLocation?.id || null;

  useEffect(() => {
    if (!selectedLocationId) {
      setSystems([]);
      setSystem("");
      return;
    }

    setSystemsLoading(true);
    const accessToken = sessionStorage.getItem("accessToken");
    const headers = accessToken ? { Authorization: `${accessToken}` } : {};
    const userId = parseInt(localStorage.getItem("id"));

    axios
      .post(`${baseUrl}/customer/systems/${selectedLocationId}`, { userId }, { headers } )
      .then((response) => {
        const fetchedSystems = response.data;
        setSystems(fetchedSystems);

        const storedSystemId = localStorage.getItem("selectedSystemId");
        if (storedSystemId) {
          const storedSystem = fetchedSystems.find(
            (sys) => sys.id === parseInt(storedSystemId)
          );
          setSystem(storedSystem ? storedSystem.system_name : fetchedSystems[0]?.system_name || "");
        } else {
          setSystem(fetchedSystems[0]?.system_name || "");
        }
      })
      .catch((error) => {
        console.error("Error fetching systems:", error);
        setSystems([]);
        setSystem("");
      })
      .finally(() => setSystemsLoading(false));
  }, [selectedLocationId]);

  // Prevent navigation until both Location and System are selected
  useEffect(() => {
    if (locationPath.pathname !== "/facility") {
      if (!location || !system) {
        navigate("/facility");
        setErrorMessage("Please select both Location and System before proceeding.");
      }
    }
  }, [location, system, locationPath.pathname, navigate]);

  // Confirm logout and navigate to login page
  const handleConfirmLogout = () => {
    localStorage.removeItem("accessToken");
    setShowLogoutAlert(false);
    navigate("/login");
  };

  // Cancel alert and stay on the same page
  const handleCancelLogout = () => {
    setShowLogoutAlert(false);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (location && system) {
      const selectedSystem = systems.find((sys) => sys.system_name === system);
      const selectedCustomer = adminCustomers.find((c) => c.name === customer);
      localStorage.setItem("customer", JSON.stringify(selectedCustomer));
      localStorage.setItem("customerId", selectedCustomer?.id || "");
      localStorage.setItem("selectedLocation", location);
      localStorage.setItem("selectedLocationId", selectedLocationId);
      localStorage.setItem("selectedSystem", system);
      localStorage.setItem("selectedSystemId", selectedSystem?.id || "");

      navigate("/dashboard");
    } else {
      setErrorMessage("Please select both Location and System.");
    }
  };

  return (
    <div className="login-wrapper">
      {(isLoading || systemsLoading) && <GlobalLoader />}
      {showLogoutAlert && (
        <LogoutAlert
          message="You will be logged out if you leave this page."
          onLogout={handleConfirmLogout}
          onCancel={handleCancelLogout}
        />
      )}
      <div className="login-overlay">
        <div className="container-fluid h-100 d-flex justify-content-around align-items-center flex-wrap trans-bg">
          <div className="login-form-box theme-container p-4 rounded shadow" style={{ maxWidth: "400px", width: "100%" }}>
            <h5 className="text-center my-2 fw-600">Welcome</h5>
            <h6 className="mb-4 text-center fnt13">Please select location and system</h6>
            <form onSubmit={handleSubmit}>
              {adminCustomers?.length > 0 && (
                <div className="mb-3">
                  <label htmlFor="customer" className="form-label fnt12">
                    Customer
                  </label>
                  <select
                    id="customer"
                    className="form-select form-control"
                    value={customer}
                    onChange={(e) => {
                      const selectedCustomerName = e.target.value;
                      setCustomer(selectedCustomerName);
                      setLocation("");
                      setSystem("");

                      const selectedCustomer = adminCustomers.find(c => c.name === selectedCustomerName);
                      if (selectedCustomer?.id) {
                        fetchLocations(selectedCustomer.id);
                      }
                    }}
                    required
                  >
                    {adminCustomers.map((cust) => (
                      <option key={cust.id} value={cust.name}>
                        {cust.name}
                      </option>
                    ))}
                  </select>
                </div>
              )}

              <div className="mb-3">
                <label htmlFor="location" className="form-label fnt12">
                  Location
                </label>
                <select
                  id="location"
                  className="form-select form-control"
                  value={location}
                  onChange={(e) => setLocation(e.target.value)}
                  required
                >
                  {locations.map((loc) => (
                    <option key={loc.id} value={loc.plant_name}>
                      {loc.plant_name}
                    </option>
                  ))}
                </select>
              </div>

              <div className="mb-3">
                <label htmlFor="system" className="form-label fnt12">
                  System
                </label>
                <select
                  id="system"
                  className="form-select form-control"
                  value={system}
                  onChange={(e) => setSystem(e.target.value)}
                  required
                  disabled={!systems.length}
                >
                  {systems.map((sys) => (
                    <option key={sys.id} value={sys.system_name}>
                      {sys.system_name}
                    </option>
                  ))}
                </select>
              </div>

              {errorMessage && (
                <div className="text-danger mb-3">{errorMessage}</div>
              )}

              <button
                type="submit"
                disabled={!customer || !location || !system }
                className="btn btn-primary w-100 mt-2"
              >
                Submit
              </button>
            </form>
          </div>

          <div className="text-center text-white mt-4 mt-lg-0 ms-lg-5">
            <img
              src={newlogo}
              alt="Cold Core Logo"
              className="img-fluid"
              style={{ maxWidth: "22vw" }}
            />
          </div>
        </div>
      </div>
    </div>
  );

};

export default Facility;
